import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Page,
  Toolbar,
  ExcelExport,
  PdfExport,
  ColumnChooser,
  Sort,
} from '@syncfusion/ej2-react-grids';
import has from 'lodash/has';
import toString from 'lodash/toString';
import { getFromLocalStorage } from 'localStorageService/LocalStorage';
import { PdfTrueTypeFont } from '@syncfusion/ej2-pdf-export';
import { calibriFont } from 'constant/PdfFont';
// constants
import { HasPermission } from 'constant/Helpers';
import { localStorageKeys } from 'constant/Enums';
// selectors
import { getPermissionsList } from 'store/app/AppSelectors';

const SubInventoryDataGrid = ({ data }) => {
  const permissionsList = useSelector((state) => getPermissionsList({ state }));
  const toolbarOptions = ['ExcelExport', 'PdfExport', 'CsvExport', 'ColumnChooser', 'Search'];
  const opsCenter = getFromLocalStorage(localStorageKeys.OPS_CENTER);
  const ref = useRef();

  const toolbarClick = (args) => {
    const pdfExportProperties = {
      theme: {
        caption: { font: new PdfTrueTypeFont(calibriFont, 10) },
        header: { font: new PdfTrueTypeFont(calibriFont, 12) },
        record: { font: new PdfTrueTypeFont(calibriFont, 9) },
      },
    };
    switch (args.item.text) {
      case 'PDF Export':
        ref.current.pdfExport(pdfExportProperties);
        break;
      case 'Excel Export':
        ref.current.excelExport();
        break;
      case 'CSV Export':
        ref.current.csvExport();
        break;
      default:
        break;
    }
  };

  const excelQueryCellInfo = (args) => {
    if (has(args.column, 'customExcelTemplate')) {
      args.value = toString(args.column.customExcelTemplate(args.data));
    }
  };

  const priceTemplate = (rowData) => (
    <span>{`${rowData.price.toFixed(opsCenter?.country?.decimalNumber ?? 3)} ${
      rowData.opsCurrency
    }`}</span>
  );

  const qtyTemplate = (rowData) => (
    <div className={`inventory-item-qty ${rowData.qty === 0 ? 'red' : 'primary'}`}>
      {rowData.qty} <br></br>
    </div>
  );

  const nameTemplate = (rowData) => (
    <a
      href={
        HasPermission(permissionsList, 'master_inventory', 'edit_master_children')
          ? `/inventory/${rowData.inventoryId}/editInventory/${rowData.id}`
          : ''
      }
      className="primary-color pointer"
      target="_blank"
      rel="noopener noreferrer"
    >
      {rowData.name}
    </a>
  );

  return (
    <div className="toolbar-table-style">
      <div className="control-pane">
        <div className="control-section">
          <GridComponent
            dataSource={data}
            toolbar={toolbarOptions}
            allowExcelExport={true}
            allowPdfExport={true}
            allowSorting={true}
            ref={ref}
            toolbarClick={toolbarClick}
            allowTextWrap={true}
            showColumnChooser={true}
            excelQueryCellInfo={excelQueryCellInfo}
            pdfQueryCellInfo={excelQueryCellInfo}
          >
            <ColumnsDirective>
              <ColumnDirective
                headerText="Name"
                template={nameTemplate}
                customExcelTemplate={(rowData) => rowData.name}
                width={150}
                textAlign="Center"
              />
              <ColumnDirective
                headerText="BarCode"
                field="barCode"
                customExcelTemplate={(rowData) => rowData.barCode}
                width={110}
                textAlign="Center"
              />
              <ColumnDirective
                headerText="Ops"
                field="opsName"
                customExcelTemplate={(rowData) => rowData.opsName}
                width={100}
                textAlign="Center"
              />
              <ColumnDirective
                headerText="Price"
                customExcelTemplate={(rowData) =>
                  `${rowData.price.toFixed(opsCenter?.country?.decimalNumber ?? 3)} ${
                    opsCenter?.country?.currency
                  }`
                }
                template={priceTemplate}
                width={120}
                textAlign="Center"
              />
              <ColumnDirective
                headerText="Available QTY"
                template={qtyTemplate}
                customExcelTemplate={(rowData) => rowData.qty}
                width={100}
                textAlign="Center"
              />
              <ColumnDirective
                headerText="Supplier"
                field="companyName"
                width={100}
                textAlign="Center"
              />
            </ColumnsDirective>
            <Inject services={[Page, Toolbar, ExcelExport, PdfExport, ColumnChooser, Sort]} />
          </GridComponent>
        </div>
      </div>
    </div>
  );
};

export default React.memo(SubInventoryDataGrid);
